import { Injectable, signal, inject, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { User, LoginCredentials, RegisterData } from '../models/user.model';
import { environment } from '../../environments/environment';

interface AuthApiResponse {
  status: string;
  data: {
    user: User;
    token: string;
  };
}

interface UserApiResponse {
  status: string;
  data: {
    user: User;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // -------------------------------------------------------------------------
  // 🪄 EL BOTÓN MÁGICO (Feature Flag)
  // -------------------------------------------------------------------------
  public USE_MOCK_DATA = environment.useMockData;

  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private hasLoadedUser = false;
  private currentUserSignal = signal<User | null>(null);
  private loadingSignal = signal<boolean>(false);

  currentUser = this.currentUserSignal.asReadonly();
  isAuthenticated = computed(() => this.currentUserSignal() !== null);
  isAdmin = computed(() => this.currentUserSignal()?.isAdmin ?? false);
  loading = this.loadingSignal.asReadonly();

  constructor() {
    this.loadCurrentUser();
  }

  async login(credentials: LoginCredentials, options?: { navigateOnSuccess?: boolean }): Promise<void> {
    const navigateOnSuccess = options?.navigateOnSuccess ?? true;

    if (this.USE_MOCK_DATA) {
      // Forzamos el seteo del usuario y la navegación
      this.currentUserSignal.set({
        id: 1,
        username: 'David Admin',
        email: 'tornetis@hotmail.com',
        isAdmin: true,
        createdAt: new Date()
      } as any);
      if (navigateOnSuccess) {
        await this.router.navigate(['/admin']); // O la ruta principal '/'
      }
      return;
    }

    this.loadingSignal.set(true);
    try {
      const response = await firstValueFrom(
        this.http.post<AuthApiResponse>(`${environment.apiUrl}/auth/login`, credentials, {
          withCredentials: true
        })
      );

      if (this.isBrowser && response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }

      this.currentUserSignal.set(response.data.user);
      if (navigateOnSuccess) {
        await this.router.navigate(['/']);
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async register(data: RegisterData): Promise<void> {
    if (this.USE_MOCK_DATA) {
      // Simulamos que el registro fue exitoso y logueamos automáticamente
      this.currentUserSignal.set({
        id: 1,
        username: data.username,
        email: data.email,
        isAdmin: true,
        createdAt: new Date()
      } as any);
      await this.router.navigate(['/admin']);
      return;
    }
    
    this.loadingSignal.set(true);
    try {
      const response = await firstValueFrom(
        this.http.post<AuthApiResponse>(`${environment.apiUrl}/auth/register`, data, {
          withCredentials: true
        })
      );

      if (this.isBrowser && response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }

      this.currentUserSignal.set(response.data.user);
      await this.router.navigate(['/']);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async loadCurrentUser(): Promise<void> {
    if (!this.isBrowser || this.hasLoadedUser) {
      return;
    }
    this.hasLoadedUser = true;

    // --- LÓGICA DEL INTERRUPTOR ---
    if (this.USE_MOCK_DATA) {
      // 🕵️‍♂️ MODO BÚNKER: Datos de Admin para pruebas locales
      this.currentUserSignal.set({
        id: 1,
        username: 'Esteban Admin',
        email: 'tornetis@hotmail.com',
        isAdmin: true, // 🔑 ¡AHORA SÍ! Esto activa los permisos
        createdAt: new Date()
      } as any); // <-- Ignoramos si faltan otros campos del modelo
      return;
    }

    // MODO PRODUCCIÓN: Llamada real
    try {
      const response = await firstValueFrom(
        this.http.get<UserApiResponse>(`${environment.apiUrl}/auth/me`, {
          withCredentials: true
        })
      );
      this.currentUserSignal.set(response.data.user);
    } catch (error) {
      if (!this.isUnauthorizedError(error)) {
        console.warn('Silenced auth/me error pending backend CORS fix');
      }
      this.currentUserSignal.set(null);
    }
  }

  async logout(): Promise<void> {
    this.currentUserSignal.set(null);
    this.hasLoadedUser = false;
    
    if (this.USE_MOCK_DATA) {
      this.router.navigate(['/login']);
      return;
    }

    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/auth/logout`, {}, {
          withCredentials: true
        })
      );
    } catch (error) {
      if (!this.isUnauthorizedError(error)) {
        console.error('Logout error:', error);
      }
    } finally {
      if (this.isBrowser) {
        localStorage.removeItem('auth_token');
      }
      this.router.navigate(['/login']);
    }
  }

  private isUnauthorizedError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }
}